import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

/**
 * NeurotypeSection highlights Eduksy's adaptation capabilities for different neurotypes.
 */
export function NeurotypeSection() {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });

  const reveal = (stagger?: number) =>
    `fade-up ${isVisible ? 'revealed' : ''}${stagger !== undefined ? '' : ''}`;

  const stagger = (i: number): React.CSSProperties =>
    ({ '--stagger': i }) as React.CSSProperties;

  return (
    <section
      id="neurotype"
      className="section neurotype-section"
      aria-label="Neurocognitive Adaptation"
      ref={sectionRef}
    >
      <div className="section-inner">
        <h2 className={`section-title ${reveal()}`}>Built for the Brains Everyone Else Ignores</h2>
        <p className={`section-subtitle ${reveal()}`} style={stagger(1)}>
          15-20% of the population is neurodivergent. Every other learning platform treats all brains the same. We don&apos;t.
        </p>

        <div className={`neurotype-cards ${reveal()}`} style={stagger(2)}>
          <div className="feature-card neurotype-card neurotype-card-adhd">
            <div className="feature-icon">
              <span className="material-symbols-outlined">electric_bolt</span>
            </div>
            <h3>Hyperfocus Is a Feature</h3>
            <p>
              Scout detects when your dopamine system locks onto a topic and rides the wave &mdash; switching content to your interests to maximize the learning window. Working memory capacity: 5 items. Chunk size: 3. Pacing: rapid during flow.
            </p>
            <div className="neurotype-stat">
              2-3x learning improvement during hyperfocus (Sonuga-Barke, 2005)
            </div>
          </div>

          <div className="feature-card neurotype-card neurotype-card-dyslexia">
            <div className="feature-icon">
              <span className="material-symbols-outlined">hearing</span>
            </div>
            <h3>Beyond Reading</h3>
            <p>
              Audio-first instruction. Haptic syllable patterns. Hesitation detection that intervenes before frustration. 20% accuracy improvement from letter spacing alone.
            </p>
            <div className="neurotype-stat">
              Phonological bottleneck detected at &gt;1500ms hesitation
            </div>
          </div>

          <div className="feature-card neurotype-card neurotype-card-combined">
            <div className="feature-icon">
              <span className="material-symbols-outlined">neurology</span>
            </div>
            <h3>The Hardest Case, Solved</h3>
            <p>
              The most constrained working memory (4 items). The smallest chunk sizes (2 words). The most repetition (2.5x multiplier). Scout adjusts every parameter to your cognitive signature.
            </p>
            <div className="neurotype-stat">
              Every threshold calibrated to published research
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
